import { CONTENT } from "@/content";
import { BorderBeam } from "@/components/ui/border-beam";
import { Reveal } from "../ui/Reveal";
import { SecHead } from "./SecHead";
import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";

type Props = { onCTA: () => void };

export function Professor({ onCTA }: Props) {
  const p = CONTENT.professor;
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["-15%", "15%"]);

  return (
    <section className="section dark professor" id="professor" ref={ref}>
      <div className="wrap">
        <SecHead label={p.label} title={p.title} dark />
        <Reveal className="prof-subwrap">
          <p className="prof-sub serif">{p.subtitle}</p>
        </Reveal>

        <div className="prof-grid">
          <motion.div 
            className="prof-figure"
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-10%" }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          >
            <motion.div 
              className="prof-photo"
              whileHover={{ scale: 1.02 }}
              transition={{ duration: 0.4 }}
            >
              <motion.img 
                src="/assets/alexandre-duotone.png" 
                alt="Prof. Alexandre Sousa" 
                style={{ y, scale: 1.15 }}
              />
              <span className="prof-photo-tick" />
            </motion.div>
            <div className="prof-figcap">
              <span className="hand">Prof. Alexandre Sousa</span>
              <span className="prof-figcap-s">Fundador da UdiSchool</span>
            </div>
          </motion.div>
          <div className="prof-text">
            <Reveal className="body prof-block" delay={1}>
              <span className="prof-block-k">Experiência internacional</span>
              {p.international.map((x, i) => (
                <p key={i}>{x}</p>
              ))}
            </Reveal>
            <Reveal className="body prof-block" delay={2}>
              <span className="prof-block-k">Formação e vivência profissional</span>
              {p.formation.map((x, i) => (
                <p key={i}>{x}</p>
              ))}
            </Reveal>
            <Reveal className="body prof-block" delay={2}>
              <span className="prof-block-k">A filosofia da UdiSchool</span>
              {p.philosophy.map((x, i) => (
                <p key={i}>{x}</p>
              ))}
            </Reveal>
          </div>
        </div>

        <div className="prof-badges">
          {p.badges.map(([k, v], i) => (
            <motion.div
              key={i}
              className="prof-badge"
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              whileInView={{ opacity: 1, scale: 1, y: 0 }}
              viewport={{ once: true, margin: "-10%" }}
              transition={{ duration: 0.5, delay: i * 0.15 }}
              whileHover={{ 
                y: -5,
                backgroundColor: "rgba(234, 179, 8, 0.05)",
                borderColor: "rgba(234, 179, 8, 0.2)"
              }}
              style={{ transition: "background-color 0.3s, border-color 0.3s" }}
            >
              <span className="prof-badge-n">{String(i + 1).padStart(2, "0")}</span>
              <h4 className="prof-badge-k">{k}</h4>
              <p className="prof-badge-v">{v}</p>
            </motion.div>
          ))}
        </div>

        <div className="prof-foot">
          <Reveal className="center">
            <button className="btn btn-gold btn-lg btn-beam btn-beam-gold" onClick={onCTA}>
              <BorderBeam />
              {p.cta} <span className="arr">→</span>
            </button>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
